import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { EngineUtils } from './engineUtils.js';
import { GameLogic } from './logic.js';
import AssetRepository from './engine-lite/assetRepository.js';
import { initKitchen, Loader } from './kitchenScene.js';
import { learningStage, isLearning } from './learningStage.js';
import { TextureOptimizer } from './textureOptimizer.js';
import { MobileControls } from './mobileControls.js';
import { initMainSecondScene } from './mainSecondScene.js';

let camera, scene, renderer, labelRenderer, composer, mouseBox, monitor, orbitControls, folderLabel;
let mouseX = 0, mouseY = 0, isAtDesk = false;
const initialRotation = new THREE.Euler();

// Глобальное состояние игры
window.gameState = { 
    camera: null, 
    scene: null, 
    persons: [], 
    video: null, 
    texture: null, 
    raycaster: null,
    monitorMaterial: null,
    startBtn: null
};

// Стартовый оверлей
document.getElementById('startButtonScene1').addEventListener('click', async () => {
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.style.display = 'none';
    await initKitchen();
});

document.getElementById('startButtonScene2').addEventListener('click', async () => {
    const overlay = document.getElementById('overlay');
    if (overlay) overlay.style.display = 'none';
    await initMainSecondScene();
});

function calculateAdaptiveX() {
    const aspect = window.innerWidth / window.innerHeight;
    return aspect < 1 ? 0.55 : 0.25;
}

export async function initMainScene() {
    const container = document.getElementById('container');
    const labelContainer = document.getElementById('labels-container') || document.body;
    
    Loader.show("Инициализация офиса...");
    
    // 1. Базовая сцена и камера
    scene = EngineUtils.createScene(0xffffff);
    camera = EngineUtils.createCamera(60, 0.1, 20, 5);
    renderer = EngineUtils.createRenderer(container);
    camera.position.set(.8, 1.2, .8);

    // 2. CSS2D рендерер для плавающих надписей
    labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(window.innerWidth, window.innerHeight);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0px';
    labelRenderer.domElement.style.pointerEvents = 'none';
    labelContainer.appendChild(labelRenderer.domElement);

    // 3. Свет и Пост-обработка
    EngineUtils.setupLighting(scene, ['p2', 'p3', 'p4']);
    composer = EngineUtils.createComposer(renderer, scene, camera);
    
    window.gameState.camera = camera; 
    window.gameState.scene = scene;

    // 4. Мобильное управление (OrbitControls)
    if (MobileControls.isTouchDevice()) {
        orbitControls = new OrbitControls(camera, renderer.domElement);
        orbitControls.target.set(0.79, 1.2, 0.8);
        orbitControls.enablePan = false;
        orbitControls.enableZoom = false;
        orbitControls.update();
    }

    // 5. Видео-текстура монитора
    const video = document.getElementById('video');
    video.play().catch(() => console.warn("Автоплей видео заблокирован"));
    const texture = new THREE.VideoTexture(video);
    texture.flipY = false;
    texture.colorSpace = THREE.SRGBColorSpace;
    window.gameState.video = video; 
    window.gameState.texture = texture;

    // 6. Загрузка ассетов
    const assetRepo = new AssetRepository();
    await assetRepo.loadAssetsFromAssetListFile("/assets/prefabsList.json");
    const modelScene2 = await assetRepo.createFromAsset("sc2");
    
    modelScene2.traverse((obj) => {
        if (obj.isMesh) {
            TextureOptimizer.optimizeMesh(obj); 
            obj.castShadow = obj.receiveShadow = true;
        }
    });
    scene.add(modelScene2);

    // 7. Поиск объектов в модели
    mouseBox = modelScene2.getObjectByName("mouse");
    const folderW = modelScene2.getObjectByName("folderW");
    
    if (mouseBox) {
        mouseBox.traverse(c => { if (c.isMesh) c.userData.interactive = true; });
    }

    // 8. Лейбл над папкой и запуск Обучения
    if (folderW) {
        folderW.userData.interactive = true;
        
        const folderDiv = document.createElement('div');
        folderDiv.className = 'world-label';
        folderDiv.innerText = "Изучить персонажей";
        folderDiv.id = "folder-instruction-label"; 
        
        folderLabel = new CSS2DObject(folderDiv);
        folderLabel.position.set(0, 0.4, 0); // Позиция над папкой
        folderW.add(folderLabel);

        // Инициализация стадии обучения с колбэком удаления лейбла
        learningStage(scene, camera, renderer, folderW, () => {
            if (folderLabel) {
                folderLabel.visible = false;
                folderW.remove(folderLabel);
                folderLabel = null;
            }
        });
    }

    // 9. Настройка монитора
    monitor = modelScene2.getObjectByName("monitor1") || modelScene2.getObjectByName("Plane002");
    if (monitor) {
        const mesh = monitor.isMesh ? monitor : monitor.getObjectByProperty('isMesh', true);
        mesh.material = new THREE.MeshPhongMaterial({ 
            map: texture, 
            emissive: 0xffffff, 
            emissiveMap: texture, 
            emissiveIntensity: 1, 
            side: THREE.DoubleSide 
        });
        window.gameState.monitorMaterial = mesh.material;
    }

    // 10. Кнопка "Начать созвоны" (3D)
    const btnTex = new THREE.TextureLoader().load('./assets/images/startBtn.png');
    const startBtnMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(0.2, 0.1), 
        new THREE.MeshStandardMaterial({ map: btnTex, transparent: true })
    );
    startBtnMesh.position.set(-0.18, 1.1, .79); 
    startBtnMesh.rotateY(Math.PI / 2);
    startBtnMesh.userData = { type: 'startCalls' };
    scene.add(startBtnMesh);
    window.gameState.startBtn = startBtnMesh;

    // 11. Инициализация логики игры
    window.gameState.raycaster = new THREE.Raycaster();
    const gameLogic = new GameLogic(); 
    await gameLogic.init();
    window.gameLogic = gameLogic; // Делаем доступным глобально

    // 12. Единый обработчик кликов по сцене
    renderer.domElement.addEventListener('click', (e) => {
        // Если идет обучение (таймер или карточки), блокируем действия со столом
        // Но НЕ делаем return сразу, чтобы клик по папке мог сработать внутри learningStage.js
        window.gameState.raycaster.setFromCamera(EngineUtils.getMouseNDC(e, renderer.domElement), camera);
        
        // Клик по папке: скрываем лейбл вручную для отзывчивости
        if (folderW) {
            const folderHit = window.gameState.raycaster.intersectObject(folderW, true);
            if (folderHit.length > 0 && folderLabel) {
                folderLabel.visible = false;
            }
        }

        if (isLearning()) return; 

        // Логика взаимодействия за столом
        if (isAtDesk) {
            const intersectsStart = window.gameState.raycaster.intersectObject(window.gameState.startBtn);
            if (intersectsStart.length > 0 && window.gameState.startBtn.visible) {
                gameLogic.startSequence();
            }
        } else if (mouseBox && window.gameState.raycaster.intersectObject(mouseBox, true).length > 0) {
            initialRotation.copy(camera.rotation);
            moveCameraToPosition(calculateAdaptiveX(), 1.1);
        }
    });

    // 13. Движение камеры мышью (Desktop)
    if (!MobileControls.isTouchDevice()) {
        camera.lookAt(new THREE.Vector3(-1, 1.08, .78));
        camera.rotation.reorder('YXZ');
        initialRotation.copy(camera.rotation);
        
        document.addEventListener('mousemove', (e) => { 
            mouseX = (e.clientX / window.innerWidth) * 2 - 1; 
            mouseY = (e.clientY / window.innerHeight) * 2 - 1; 
        });
    }
    
    window.addEventListener('resize', () => {
        EngineUtils.handleResize(camera, renderer, composer);
        if (labelRenderer) labelRenderer.setSize(window.innerWidth, window.innerHeight);
    });

    Loader.hide(); 
    renderer.setAnimationLoop(animate);
}

// Плавное перемещение к монитору
function moveCameraToPosition(targetX, targetY) {
    const duration = 800;
    const startX = camera.position.x;
    const startY = camera.position.y;
    const startTime = performance.now();
    
    isAtDesk = false;
    if (orbitControls) orbitControls.enabled = false;

    function animateMove(currentTime) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = progress * (2 - progress);

        camera.position.x = startX + (targetX - startX) * ease;
        camera.position.y = startY + (targetY - startY) * ease;

        if (progress < 1) {
            requestAnimationFrame(animateMove);
        } else {
            isAtDesk = true;
            if (mouseBox) mouseBox.visible = false;
            camera.rotation.reorder('YXZ');
            initialRotation.copy(camera.rotation);
            
            if (orbitControls) {
                orbitControls.enabled = true;
                orbitControls.target.set(camera.position.x - 0.01, camera.position.y, camera.position.z);
                orbitControls.update();
            }
        }
    }
    requestAnimationFrame(animateMove);
}

export function animate() {
    if (MobileControls.isTouchDevice()) {
        if (orbitControls && orbitControls.enabled) orbitControls.update();
    } else {
        // Легкое покачивание камеры (Desktop)
        const targetRotX = initialRotation.x + (-mouseY * 0.03);
        const targetRotY = initialRotation.y + (-mouseX * 0.05);
        camera.rotation.x += (targetRotX - camera.rotation.x) * 0.1;
        camera.rotation.y += (targetRotY - camera.rotation.y) * 0.1;
    }

    // Рендеринг 3D
    composer.render();
    
    // Рендеринг HTML-надписей
    if (labelRenderer) {
        labelRenderer.render(scene, camera);
    }
}